import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, Calculator, Target, FileSearch, BookOpen, UserCircle2, ArrowRight } from "lucide-react";
import { api, waUrl } from "../lib/api";
import Logo from "./Logo";

const NAV_ITEMS = [
    { id: "smart-search", label: "Cari Kampus", icon: Search, tid: "nav-cari-kampus" },
    { id: "roi", label: "Hitung Biaya & ROI", icon: Calculator, tid: "nav-roi" },
    { id: "screener", label: "Cek Peluang Lolos", icon: Target, tid: "nav-peluang" },
    { id: "screener", label: "Cek Berkas", icon: FileSearch, tid: "nav-berkas" },
    { id: "articles", label: "Artikel", icon: BookOpen, tid: "nav-artikel" },
    { id: "portal", label: "Portal Klien", icon: UserCircle2, tid: "nav-portal" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <header
            data-testid="main-navbar"
            className={`sticky top-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm"
                    : "bg-white/85 backdrop-blur-xl border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 flex items-center justify-between h-20 gap-4">
                <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
                    <Logo size={40} />
                </Link>

                <nav className="hidden xl:flex items-center gap-1 flex-shrink-0">
                    {NAV_ITEMS.map(({ id, label, icon: Icon, tid }) => (
                        <button
                            key={tid}
                            onClick={() => scrollTo(id)}
                            data-testid={tid}
                            className="group inline-flex items-center gap-2 px-3 py-2 rounded-full text-[13px] font-medium text-slate-700 hover:text-[#0A1A2F] hover:bg-[#F5A623]/10 transition-all whitespace-nowrap"
                        >
                            <Icon className="w-3.5 h-3.5 text-[#F5A623] group-hover:scale-110 transition-transform" strokeWidth={2.25} />
                            <span>{label}</span>
                        </button>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                    {user ? (
                        <button
                            data-testid="navbar-dashboard-btn"
                            onClick={() => navigate("/dashboard")}
                            className="inline-flex items-center gap-1.5 border-2 border-slate-200 hover:border-[#0A1A2F] text-[#0A1A2F] rounded-full px-4 py-2 font-semibold text-[13px] transition-all"
                        >
                            Dashboard
                        </button>
                    ) : (
                        <button
                            data-testid="navbar-login-btn"
                            onClick={() => navigate("/login")}
                            className="hidden md:inline-flex items-center gap-1.5 text-slate-600 hover:text-[#0A1A2F] rounded-full px-3 py-2 font-medium text-[13px] transition-all"
                        >
                            Masuk Portal
                        </button>
                    )}
                    <button
                        data-cta="cta_nav_consultation"
                        data-testid="cta-nav-consultation"
                        onClick={() => scrollTo("smart-search")}
                        className="inline-flex items-center gap-2 bg-[#F5A623] text-[#0A1A2F] hover:bg-[#D98F1B] rounded-full px-5 py-2.5 font-bold text-sm transition-all shadow-[0_4px_14px_0_rgba(245,166,35,0.35)]"
                    >
                        Mulai Konsultasi Gratis
                    </button>
                </div>

                <button
                    data-testid="navbar-mobile-toggle"
                    className="xl:hidden p-2 -mr-2"
                    onClick={() => setOpen(!open)}
                    aria-label={open ? "Tutup menu" : "Buka menu"}
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="xl:hidden border-t border-slate-100 bg-white px-5 py-4 flex flex-col gap-1.5" data-testid="navbar-mobile-menu">
                    {NAV_ITEMS.map(({ id, label, icon: Icon, tid }) => (
                        <button
                            key={tid}
                            onClick={() => scrollTo(id)}
                            data-testid={`m-${tid}`}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#F5A623]/10 transition text-left"
                        >
                            <span className="w-9 h-9 rounded-lg bg-[#0A1A2F] flex items-center justify-center">
                                <Icon className="w-4 h-4 text-[#F5A623]" />
                            </span>
                            <span className="font-medium text-[#0A1A2F]">{label}</span>
                        </button>
                    ))}
                    <div className="pt-3 mt-2 border-t border-slate-100 flex flex-col gap-2">
                        <button onClick={() => scrollTo("smart-search")} className="w-full bg-[#F5A623] text-[#0A1A2F] rounded-full px-5 py-3 font-bold text-sm" data-testid="m-cta">Mulai Konsultasi Gratis</button>
                        {user ? (
                            <button onClick={() => navigate("/dashboard")} className="w-full border-2 border-[#0A1A2F] text-[#0A1A2F] rounded-full px-5 py-2.5 font-semibold text-sm" data-testid="m-dashboard">Dashboard</button>
                        ) : (
                            <button onClick={() => navigate("/login")} className="w-full border-2 border-slate-200 text-slate-700 rounded-full px-5 py-2.5 font-medium text-sm" data-testid="m-login">Masuk Portal</button>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
